import { Injectable, Inject, PLATFORM_ID, TransferState, makeStateKey } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { isPlatformServer } from '@angular/common';
import { catchError, firstValueFrom, of, timeout } from 'rxjs';
import { TenantService } from '../tenant/tenant';
import { API_BASE_URL } from '../config/api-config';
import { environment } from '../../../environments/environment';
import { IDocumentTypeResponse, IDocumentType } from '../models/documents.model';

const DOC_TYPES_KEY = makeStateKey<IDocumentType[]>('document-types');

@Injectable({
    providedIn: 'root'
})
export class DocumentTypeService {
    public documentTypes: IDocumentType[] = [];

    constructor(
        private http: HttpClient,
        private transferState: TransferState,
        @Inject(PLATFORM_ID) private platformId: Object,
        private tenantService: TenantService,
        @Inject(API_BASE_URL) private apiBaseUrl: string
    ) { }

    async loadDocumentTypes(): Promise<void> {
        let domain = this.tenantService.getDomain();

        if (!domain || domain === 'localhost' || domain === '127.0.0.1' || domain.includes("efrx")) {
            domain = environment.fallbackDomain;
        }

        // 1. Browser check
        if (this.transferState.hasKey(DOC_TYPES_KEY)) {
            this.documentTypes = this.transferState.get(DOC_TYPES_KEY, []);
            this.transferState.remove(DOC_TYPES_KEY);
            return;
        }

        // 2. Fetch via internal proxy
        try {
            const response = await firstValueFrom(
                this.http.get<IDocumentTypeResponse>(`${this.apiBaseUrl}/internal-api/list-document-types/${domain}`).pipe(
                    timeout(5000),
                    catchError(() => of({ data: [] } as IDocumentTypeResponse))
                )
            );

            this.documentTypes = response?.data || [];

            // 3. Save to TransferState on server
            if (isPlatformServer(this.platformId)) {
                this.transferState.set(DOC_TYPES_KEY, this.documentTypes);
            }
        } catch (error) {
            console.error(`Failed to load document types for domain: ${domain}`, error);
            this.documentTypes = [];
        }
    }
}
